/**
 * ResultBand — the ONE result-band primitive for the Product data lookup
 * card's Test / preview affordances (Phase 56.1 / BLU-536 Plan 08).
 *
 * Consolidates the green/amber/red/gray band palette that was previously
 * duplicated three times (TestResultBand in ProductDataLookupCard,
 * SkuTestResultBand in SkuTestSection, and inline amber tokens in
 * SweepStatusBlock — whose comment filed this extraction as a P2 follow-up).
 * The palette lives HERE and nowhere else; new band-like surfaces import
 * `ResultBand` (or `BAND_PALETTE` for non-band containers like
 * SweepStatusBlock's status blocks).
 *
 * Accessibility discipline this shell carries (hard-won — do not regress):
 *
 *   - `role="status"` + `aria-live="polite"`: the band announces its content
 *     to screen readers when it mounts or its content changes.
 *   - SINGLE MOUNT PER RESULT: callers MUST render at most one ResultBand
 *     per result slot and `key` it on the RESULT IDENTITY (sku + status +
 *     distinguishing fields). A NEW result then produces a fresh element
 *     (entrance animation replays, SR announces once); a phase transition
 *     (`showing → idle`) on the SAME result keeps the same element so
 *     `aria-live` does NOT re-announce. Earlier two-branch conditionals
 *     double-mounted and triggered SR re-reads on every phase tick.
 *   - No focus moves: the band never steals focus; the polite live region
 *     is the only announcement channel.
 *
 * Motion: entrance animation is gated on `animateIn` (driven by the caller's
 * `useStaggeredTestPhase` `phase === 'showing'`) and always pairs
 * `motion-safe:` classes with `motion-reduce:animate-none` (DESIGN.md §5
 * hard rule).
 */

import type { ReactNode } from 'react';

export type BandKind = 'green' | 'amber' | 'red' | 'gray';

/**
 * Semantic band palette — border + background + text, light and dark.
 * Single source of truth; verbatim tokens from the pre-extraction bands.
 */
export const BAND_PALETTE: Record<BandKind, string> = {
  green:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100',
  amber:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100',
  red: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-100',
  gray:
    'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-100',
};

const ENTRANCE_ANIMATION =
  'motion-safe:animate-in motion-safe:fade-in-50 motion-safe:slide-in-from-bottom-1 motion-reduce:animate-none motion-reduce:transition-none';
const NO_ANIMATION = 'motion-reduce:animate-none motion-reduce:transition-none';

export interface ResultBandProps {
  kind: BandKind;
  /** Apply the entrance animation classes. Defaults to true. */
  animateIn?: boolean;
  /** Extra classes appended after the shell's own (rare; layout-only). */
  className?: string;
  children: ReactNode;
}

export function ResultBand({
  kind,
  animateIn = true,
  className,
  children,
}: ResultBandProps) {
  const animation = animateIn ? ENTRANCE_ANIMATION : NO_ANIMATION;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`${animation} rounded-md border p-3 text-sm space-y-2 ${BAND_PALETTE[kind]}${
        className ? ` ${className}` : ''
      }`}
    >
      {children}
    </div>
  );
}
